import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  DictationFeedbackAnalyticsPorts,
  DictationFeedbackSubmittedEvent,
} from '@heidi/dictate/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * SCR-7608 explicitly needs the original feedback funnel in Amplitude. The
 * confirmation interactions were added later and follow the Snowplow-first
 * policy, so they opt out of Amplitude explicitly.
 */
export const trackDictationFeedbackPromptViewed = (): void => {
  track({
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: ScreenName.DictationFeedbackPrompt },
    options: { amplitude: true },
  });
};

export const trackDictationFeedbackReasonPickerViewed = (): void => {
  track({
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: ScreenName.DictationFeedbackReasonPicker },
    options: { amplitude: true },
  });
};

export const trackDictationFeedbackNotNowClicked: DictationFeedbackAnalyticsPorts['trackNotNowClicked'] =
  () => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.DictationFeedbackReasonPicker,
        action: ButtonAction.DictationFeedbackNotNow,
        label: 'Not now',
      },
      options: { amplitude: true },
    });
  };

export const trackDictationFeedbackSeeHistoryClicked: DictationFeedbackAnalyticsPorts['trackSeeHistoryClicked'] =
  (surface) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.DictationFeedbackConfirmation,
        action: ButtonAction.DictationFeedbackSeeHistory,
        label: 'See history',
        entry_point: surface,
      },
      options: { amplitude: false },
    });
  };

export const trackDictationLogsFeedbackReasonPickerViewed: DictationFeedbackAnalyticsPorts['trackLogsReasonPickerViewed'] =
  () => {
    track({
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.DictationLogsFeedbackReasonPicker },
      options: { amplitude: true },
    });
  };

export const trackDictationLogsFeedbackCancelClicked: DictationFeedbackAnalyticsPorts['trackLogsCancelClicked'] =
  () => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.DictationLogsFeedbackReasonPicker,
        action: ButtonAction.DictationLogsFeedbackCancel,
        label: 'Cancel',
      },
      options: { amplitude: true },
    });
  };

/**
 * The ticket explicitly requires this existing interaction in Amplitude, so
 * its established Amplitude route remains enabled. Schema-native feedback
 * fields also keep the existing Snowplow event useful; the additional cohort
 * fields are required by the Amplitude analysis for this rollout.
 */
export const trackDictationFeedbackSubmitted = (
  event: DictationFeedbackSubmittedEvent
): void => {
  track({
    event_name: TrackingEventName.FeedbackSubmitted,
    props: {
      type: 'thumbs',
      value: event.rating === 'thumb_up' ? 'up' : 'down',
      target_type: 'dictation',
      target_id: event.dictation_id,
      source: event.platform,
      platform: event.platform,
      surface: event.surface,
      ...(event.experiment_variant
        ? { experiment_variant: event.experiment_variant }
        : {}),
      input_languages: event.input_languages,
      output_languages: event.output_languages,
      ...(event.region ? { region: event.region } : {}),
      feedback_state: event.feedback_state,
      rating: event.rating,
      feedback_rating_reasons: event.reasons,
      has_feedback_text: event.has_feedback_text,
    },
    options: { amplitude: true },
  });
};

export const dictationFeedbackAnalyticsPorts: DictationFeedbackAnalyticsPorts = {
  trackPromptViewed: trackDictationFeedbackPromptViewed,
  trackReasonPickerViewed: trackDictationFeedbackReasonPickerViewed,
  trackNotNowClicked: trackDictationFeedbackNotNowClicked,
  trackSeeHistoryClicked: trackDictationFeedbackSeeHistoryClicked,
  trackLogsReasonPickerViewed: trackDictationLogsFeedbackReasonPickerViewed,
  trackLogsCancelClicked: trackDictationLogsFeedbackCancelClicked,
  trackSubmitted: trackDictationFeedbackSubmitted,
};
